import { EntityExtraVersionRef, SandboxFileKeyFileName } from "@amzn/altar-sds-client";
import axios, { AxiosRequestConfig } from "axios";
import { APIOutput, IAdvancedList, MIMEType } from "@amzn/ask-legal-domain";
import { FileUpload, Flashbar, FlashbarProps, FormField, Spinner, SpinnerProps } from "@amzn/awsui-components-react";
import * as React from "react";
import { AppContext } from "../../../setup/context";
import { Builder } from "builder-pattern";
import { FileUtils } from "../../../utils/file-utils";
import { SDSUtils } from "../../../utils/sds-utils";

const ITEM_ATTACHMENT_FILE_SIZE_LIMIT_MB = 5000; // 5GB

interface FileValidationResult {
    isValid: boolean;
    reason?: string;
}

export function AttachmentUpload(props: {
    itemRef: EntityExtraVersionRef;
    setUploadingSandboxFiles: (val: boolean) => void;
    onUpdated?: (sandboxFiles: SandboxFileKeyFileName[]) => void;
}) {
    const context = React.useContext(AppContext);
    const [spinnerProps, setSpinnerProps] = React.useState<SpinnerProps>();
    const [flashbarProps, setFlashbarProps] = React.useState<Pick<FlashbarProps, "items">>();
    const [files, setFiles] = React.useState<File[]>([]);

    async function validateFiles(files: File[]): Promise<File[]> {
        const validationResults = new Map<string, FileValidationResult>();
        for (const file of files) {
            const result = await isValidFile(file);
            if (!result.isValid) {
                validationResults.set(file.name, result);
            }
        }

        if (validationResults.size > 0) {
            setFlashbarProps({
                items: Array.from(validationResults.entries()).map(([fileName, result]) => ({
                    type: "error",
                    content: `${fileName}: ${result.reason}`
                }))
            });
        } else {
            setFlashbarProps({ items: [] });
        }

        return files.filter((f) => !validationResults.has(f.name));
    }

    async function isValidFile(file: File): Promise<FileValidationResult> {
        const extension = FileUtils.getExtension(file.name);

        // 1. check if file is part of allowed extensions
        if (!allowedFileTypes.includes(extension)) {
            return {
                isValid: false,
                reason: `File type '${extension}' is not allowed. Supported types: ${allowedFileTypes.join(", ")}`
            };
        }

        // 2. check if file extension matches file mime type
        if (!MIMEType[extension]?.includes(file.type)) {
            return {
                isValid: false,
                reason: `File MIME type '${file.type}' does not match extension '${extension}'`
            };
        }

        // 3. perform file signature check
        const validFileSignature = await FileUtils.isValidFileSignature(file);
        if (!validFileSignature) {
            return {
                isValid: false,
                reason: "File signature verification failed. The file content does not match its extension"
            };
        }

        // 4. check file size
        if (file.size > ITEM_ATTACHMENT_FILE_SIZE_LIMIT_MB * 1000000) {
            const fileSizeMB = (file.size / 1000000).toFixed(2);
            return {
                isValid: false,
                reason: `File size (${fileSizeMB}MB) exceeds maximum allowed size of ${ITEM_ATTACHMENT_FILE_SIZE_LIMIT_MB}MB`
            };
        }

        return { isValid: true };
    }

    async function uploadAttachments(files: File[]) {
        props.setUploadingSandboxFiles(true);
        setSpinnerProps({});
        const validFiles = await validateFiles(files);
        // validation failure - don't proceed with upload
        if (validFiles.length !== files.length) {
            props.setUploadingSandboxFiles(false);
            setSpinnerProps(undefined);
            setFiles([]);
            return;
        }

        let sandboxFiles: SandboxFileKeyFileName[] = [];
        for await (const file of files) {
            const putSignedUrlOutput = await context
                .getAdvancedListAPI()
                .generateSandboxPutUrl(
                    Builder<IAdvancedList.GenInstanceSandboxFilePutUrlInput>()
                        .by(SDSUtils.getAmazonPersonRef(context.userId))
                        .contentType(file.type)
                        .fileName(file.name)
                        .instanceId(props.itemRef.entityExtraRef.entityRef.repositoryRef.repositoryId!)
                        .build()
                );
            const output = APIOutput.fromRaw<IAdvancedList.GenInstanceSandboxFilePutUrlOutput>(putSignedUrlOutput.data);
            if (output.isErr()) {
                setSpinnerProps(undefined);
                setFlashbarProps({
                    items: [
                        {
                            type: "error",
                            content: output.err.message
                        }
                    ]
                });
                props.setUploadingSandboxFiles(false);
            } else {
                const req: AxiosRequestConfig = {
                    url: output.data.url,
                    method: "PUT",
                    data: file
                };
                await axios(req);
                sandboxFiles.push({
                    fileKey: output.data.fileKey,
                    fileName: file.name
                });
            }
        }
        setSpinnerProps(undefined);
        setFlashbarProps({
            items: []
        });
        props.onUpdated(sandboxFiles);
        props.setUploadingSandboxFiles(false);
    }

    React.useEffect(() => {
        if (files.length) {
            uploadAttachments(files);
        }
    }, [files]);

    return (
        <>
            {spinnerProps && <Spinner />}
            {flashbarProps && <Flashbar {...flashbarProps} />}
            <FormField
                label={<h5>Upload Attachments</h5>}
                description={
                    <>
                        <div>You can also drop files here to upload</div>
                        <br />
                        <div>
                            Supported file types include:{" "}
                            {Object.keys(allowedFileTypes)
                                .map((e) => allowedFileTypes[e])
                                .join(", ")}
                        </div>
                        <div>Attachments support file sizes up to {ITEM_ATTACHMENT_FILE_SIZE_LIMIT_MB}MB</div>
                    </>
                }
            >
                <FileUpload
                    onChange={({ detail }) => {
                        setFiles(detail.value);
                    }}
                    multiple
                    value={files}
                    i18nStrings={{
                        uploadButtonText: (e) => (e ? "Choose files" : "Choose file"),
                        dropzoneText: (e) => (e ? "Drop files to upload" : "Drop file to upload"),
                        removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                        limitShowFewer: "Show fewer files",
                        limitShowMore: "Show more files",
                        errorIconAriaLabel: "Error"
                    }}
                    showFileSize
                    tokenLimit={5}
                />
            </FormField>
        </>
    );
}

export function AttachmentUploadV2(props: {
    instanceId: string;
    setUploadingSandboxFiles: (val: boolean) => void;
    onUpdated?: (sandboxFiles: SandboxFileKeyFileName[]) => void;
}) {
    const context = React.useContext(AppContext);

    const [files, setFiles] = React.useState<File[]>([]);
    const [flashbarProps, setFlashbarProps] = React.useState<Pick<FlashbarProps, "items">>();
    const [spinnerProps, setSpinnerProps] = React.useState<{}>();

    async function validateFiles(files: File[]): Promise<File[]> {
        const validationResults = new Map<string, FileValidationResult>();

        for (const file of files) {
            const result = await isValidFile(file);
            if (!result.isValid) {
                validationResults.set(file.name, result);
            }
        }

        if (validationResults.size > 0) {
            setFlashbarProps({
                items: Array.from(validationResults.entries()).map(([fileName, result]) => ({
                    type: "error",
                    content: `${fileName}: ${result.reason}`
                }))
            });
        } else {
            setFlashbarProps({ items: [] });
        }

        return files.filter((f) => !validationResults.has(f.name));
    }

    async function isValidFile(file: File): Promise<FileValidationResult> {
        const extension = FileUtils.getExtension(file.name);

        if (!allowedFileTypes.includes(extension)) {
            return {
                isValid: false,
                reason: `File type '${extension}' is not allowed. Supported types: ${allowedFileTypes.join(", ")}`
            };
        }

        if (!MIMEType[extension]?.includes(file.type)) {
            return {
                isValid: false,
                reason: `File MIME type '${file.type}' does not match extension '${extension}'`
            };
        }

        if (!(await FileUtils.isValidFileSignature(file))) {
            return {
                isValid: false,
                reason: "File signature verification failed. The file content does not match its extension"
            };
        }

        if (file.size > ITEM_ATTACHMENT_FILE_SIZE_LIMIT_MB * 1000000) {
            const fileSizeMB = (file.size / 1000000).toFixed(2);
            return {
                isValid: false,
                reason: `File size (${fileSizeMB}MB) exceeds maximum allowed size of ${ITEM_ATTACHMENT_FILE_SIZE_LIMIT_MB}MB`
            };
        }

        return { isValid: true };
    }

    async function uploadAttachments(files: File[]) {
        props.setUploadingSandboxFiles(true);
        setSpinnerProps({});
        const validFiles = await validateFiles(files);

        if (validFiles.length !== files.length) {
            props.setUploadingSandboxFiles(false);
            setSpinnerProps(undefined);
            setFiles([]);
            return;
        }

        const sandboxFiles: SandboxFileKeyFileName[] = [];
        for await (const file of validFiles) {
            const urlOutput = await context
                .getAdvancedListAPI()
                .generateSandboxPutUrl(
                    Builder<IAdvancedList.GenInstanceSandboxFilePutUrlInput>()
                        .by(SDSUtils.getAmazonPersonRef(context.userId))
                        .contentType(file.type)
                        .fileName(file.name)
                        .instanceId(props.instanceId)
                        .build()
                );

            const output = APIOutput.fromRaw<IAdvancedList.GenInstanceSandboxFilePutUrlOutput>(urlOutput.data);
            if (output.isErr()) {
                setFlashbarProps({
                    items: [{ type: "error", content: output.err.message }]
                });
                props.setUploadingSandboxFiles(false);
                setSpinnerProps(undefined);
                return;
            }

            const putReq: AxiosRequestConfig = {
                url: output.data.url,
                method: "PUT",
                data: file
            };
            await axios(putReq);

            sandboxFiles.push({
                fileKey: output.data.fileKey,
                fileName: file.name
            });
        }

        setSpinnerProps(undefined);
        setFlashbarProps({ items: [] });
        props.onUpdated?.(sandboxFiles);
        props.setUploadingSandboxFiles(false);
    }

    React.useEffect(() => {
        if (files.length) {
            uploadAttachments(files);
        }
    }, [files]);

    return (
        <>
            {spinnerProps && <Spinner />}
            {flashbarProps && <Flashbar {...flashbarProps} />}
            <FormField
                label="Upload Attachments"
                description={
                    <>
                        <div>Drop or select files. Max {ITEM_ATTACHMENT_FILE_SIZE_LIMIT_MB}MB each.</div>
                        <div>Allowed types: {allowedFileTypes.join(", ")}</div>
                    </>
                }
            >
                <FileUpload
                    onChange={({ detail }) => setFiles(detail.value)}
                    multiple
                    value={files}
                    i18nStrings={{
                        uploadButtonText: (e) => (e ? "Choose files" : "Choose file"),
                        dropzoneText: (e) => (e ? "Drop files to upload" : "Drop file to upload"),
                        removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                        limitShowFewer: "Show fewer files",
                        limitShowMore: "Show more files",
                        errorIconAriaLabel: "Error"
                    }}
                    showFileSize
                    tokenLimit={5}
                />
            </FormField>
        </>
    );
}

// TODO - export allowed file types list from SDS
// Derived from destination bucket resource policy
// https://code.amazon.com/packages/ALTARSimpleDomainServiceCDK/blobs/f4dd831c0664d59f26638a619c9de00d8ba1cb6f/--/lib/stack/infra-stack.ts#L539-L552,L623-L636
const allowedFileTypes = [
    "txt",
    "jpeg",
    "png",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "csv",
    "ppt",
    "pptm",
    "pptx",
    "rtf",
    "pdf",
    "html",
    "json"
];
